import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { FC, ReactNode, useEffect, useState } from 'react'
import LazyLoad from 'react-lazy-load'

import Button from '@/components/Button'
import LangSwitcher from '@/components/LangSwitcher'
import Link from '@/components/Link'
import Logo from '@/components/Logo'
import Menu from '@/components/Menu'

import styles from './Main.module.scss'
import { useUser } from '@/bus/user'
import { routes } from '@/config'
import Header from '@/layouts/Header'

const DefaultFooter = dynamic(
  () => import('@/layouts/Footer').then(({ Footer }) => Footer),
  {
    ssr: false
  }
)

interface LayoutProps {
  children: ReactNode
  footer?: ReactNode
  header?: ReactNode
}

export const Main: FC<LayoutProps> = ({
  children,
  footer = null,
  header = null
}) => {
  const [animationStart, setAnimationStart] = useState(false)

  const { isAuthorized } = useUser()
  const { t } = useTranslation()

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    timeout = setTimeout(() => {
      setAnimationStart(true)
    }, 300)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  return (
    <main
      className={clsx(styles['main'], {
        ['animation-start']: animationStart
      })}
    >
      {header ? (
        header
      ) : (
        <Header
          before={
            <>
              <Logo />
              <LangSwitcher />
            </>
          }
          after={
            isAuthorized ? (
              <Button
                href={{ pathname: routes.SERVICES, spa: false }}
                as={Link}
              >
                {t('forms:buttons.services.label')}
              </Button>
            ) : (
              <>
                <Button variant="text" as={Link} href={routes.LOGIN}>
                  {t('forms:buttons.signin.label')}
                </Button>
                <Button as={Link} href={routes.REGISTER}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('forms:buttons.signup.label')
                    }}
                  />
                </Button>
              </>
            )
          }
        >
          <Menu />
        </Header>
      )}
      <section className={styles['main__content']}>{children}</section>

      <LazyLoad className={styles['main__lazy-footer']}>
        {footer ? footer : <DefaultFooter className={styles['main__footer']} />}
      </LazyLoad>
    </main>
  )
}
